import { Button } from '@progress/kendo-react-buttons';
import { DatePicker, DateTimePicker } from '@progress/kendo-react-dateinputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { Checkbox, Input, NumericTextBox, TextArea } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import dayjs from 'dayjs';
import { FormEvent, useState } from 'react';
import CustomerInput from 'components/CustomerInput';
import { fetchApi } from 'services/api';
import { JsonResponse } from 'TypeGen/json-response';
import { CustomerCollectionAlertStatus } from 'TypeGen/AccountsReceivable/Collections/customer-collection-alert-status';
import { CustomerCollectionContactMethod } from 'TypeGen/AccountsReceivable/Collections/customer-collection-contact-method';
import { ViewModel as CustomerCollectionActivityViewModel } from 'TypeGen/AccountsReceivable/Collections/view-model';
import { alertStatusOptions } from './alertStatuses';
import { contactMethodOptions } from './contactMethods';

type Props = {
  activity?: CustomerCollectionActivityViewModel | null;
  customerId?: number;
  onClose: () => void;
  onSaved: () => void;
};

type ActivityForm = {
  ActivityDateTime: Date | null;
  AlertStatus: CustomerCollectionAlertStatus | null;
  CustomerID: number;
  CustomerNumber: string;
  ContactPerson: string;
  ContactMethod: CustomerCollectionContactMethod;
  ContactReached: boolean;
  AmountPastDue: number | null;
  PromiseToPayDate: Date | null;
  PromiseAmount: number | null;
  Note: string;
};

const toPromiseDate = (value?: string | null) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

const CollectionActivityDialog = ({ activity, customerId = 0, onClose, onSaved }: Props) => {
  const isCustomerScoped = customerId > 0;
  const [form, setForm] = useState<ActivityForm>({
    ActivityDateTime: activity?.ActivityDateTime ? dayjs.utc(activity.ActivityDateTime).toDate() : new Date(),
    AlertStatus: activity && activity.AlertStatus !== CustomerCollectionAlertStatus.Unknown
      ? activity.AlertStatus
      : null,
    CustomerID: isCustomerScoped ? customerId : activity?.CustomerID ?? 0,
    CustomerNumber: activity?.CustomerNumber ?? '',
    ContactPerson: activity?.ContactPerson ?? '',
    ContactMethod: activity?.ContactMethod ?? CustomerCollectionContactMethod.CustomerEmail,
    ContactReached: activity?.ContactReached ?? false,
    AmountPastDue: activity?.AmountPastDue ?? null,
    PromiseToPayDate: toPromiseDate(activity?.PromiseToPayDate),
    PromiseAmount: activity?.PromiseAmount ?? null,
    Note: activity?.Note ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const save = async (event?: FormEvent) => {
    event?.preventDefault();

    const nextErrors: string[] = [];
    if (!form.ActivityDateTime || Number.isNaN(form.ActivityDateTime.getTime())) {
      nextErrors.push('Activity date and time is required.');
    }
    if (form.AlertStatus == null) {
      nextErrors.push('Alert status is required.');
    }
    if (form.CustomerID <= 0) {
      nextErrors.push('Select a customer from the customer search results.');
    }
    setErrors(nextErrors);
    if (nextErrors.length > 0) return;

    setSaving(true);
    try {
      const response: JsonResponse = await fetchApi('/api/AccountsReceivable/SaveCollectionActivity', {
        CustomerCollectionActivityID: activity?.CustomerCollectionActivityID ?? 0,
        ActivityDateTime: form.ActivityDateTime!.toISOString(),
        AlertStatus: form.AlertStatus,
        CustomerID: form.CustomerID,
        ContactPerson: form.ContactPerson,
        ContactMethod: form.ContactMethod,
        ContactReached: form.ContactReached,
        AmountPastDue: form.AmountPastDue,
        PromiseToPayDate: form.PromiseToPayDate ? dayjs(form.PromiseToPayDate).format('YYYY-MM-DD') : null,
        PromiseAmount: form.PromiseAmount,
        Note: form.Note,
        RowVersion: activity?.RowVersion ?? null,
      }, 'POST');

      if (!response.Success) {
        setErrors([response.ErrorMessage || 'Unable to save the collection activity.']);
        return;
      }

      onSaved();
    } catch {
      setErrors(['Unable to save the collection activity. Please try again.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={activity ? 'Edit Collection Activity' : 'New Collection Activity'}
      onClose={onClose}
      className="dialog-w800"
    >
      <form onSubmit={save}>
        {errors.length > 0 && (
          <div className="alert alert-danger" role="alert">
            {errors.map(error => <div key={error}>{error}</div>)}
          </div>
        )}

        <div className="row">
          <div className={`${isCustomerScoped ? 'col-md-6' : 'col-md-4'} mb-3`}>
            <Label editorId="collection-activity-date">Activity Date &amp; Time <span className="text-danger">*</span></Label>
            <DateTimePicker
              id="collection-activity-date"
              value={form.ActivityDateTime}
              onChange={event => setForm(current => ({ ...current, ActivityDateTime: event.value }))}
              format="MM/dd/yyyy hh:mm a"
              width="100%"
            />
          </div>
          <div className={`${isCustomerScoped ? 'col-md-6' : 'col-md-4'} mb-3`}>
            <Label editorId="collection-alert-status">Alert Status <span className="text-danger">*</span></Label>
            <DropDownList
              id="collection-alert-status"
              data={alertStatusOptions}
              dataItemKey="ID"
              textField="Name"
              defaultItem={{ ID: null, Name: 'Select alert status' }}
              value={alertStatusOptions.find(x => x.ID === form.AlertStatus) ?? null}
              onChange={event => setForm(current => ({ ...current, AlertStatus: event.value?.ID ?? null }))}
              style={{ width: '100%' }}
            />
          </div>
          {!isCustomerScoped && <div className="col-md-4 mb-3">
            <Label>Customer <span className="text-danger">*</span></Label>
            <div className="input-group">
              <CustomerInput
                CustomerID={form.CustomerID}
                CustomerNumber={form.CustomerNumber}
                placeholder="Search by customer ID or name"
                onChange={customer => setForm(current => ({
                  ...current,
                  CustomerID: customer.CustomerID,
                  CustomerNumber: customer.CustomerNumber,
                }))}
              />
            </div>
          </div>}
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <Label editorId="collection-contact-person">Contact Person</Label>
            <Input
              id="collection-contact-person"
              value={form.ContactPerson}
              maxLength={50}
              onChange={event => setForm(current => ({ ...current, ContactPerson: event.value }))}
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-md-6 mb-3">
            <Label editorId="collection-contact-method">Contact Method</Label>
            <DropDownList
              id="collection-contact-method"
              data={contactMethodOptions}
              dataItemKey="ID"
              textField="Name"
              value={contactMethodOptions.find(x => x.ID === form.ContactMethod)}
              onChange={event => setForm(current => ({ ...current, ContactMethod: event.value.ID }))}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="row align-items-end">
          <div className="col-md-4 mb-3">
            <Label editorId="collection-amount-past-due">Amount Past Due</Label>
            <NumericTextBox
              id="collection-amount-past-due"
              value={form.AmountPastDue}
              min={0}
              format="c2"
              spinners={false}
              onChange={event => setForm(current => ({ ...current, AmountPastDue: event.value }))}
              width="100%"
            />
          </div>
          <div className="col-md-4 mb-3">
            <Label editorId="collection-promise-date">Promise to Pay Date</Label>
            <DatePicker
              id="collection-promise-date"
              value={form.PromiseToPayDate}
              onChange={event => setForm(current => ({ ...current, PromiseToPayDate: event.value }))}
              format="MM/dd/yyyy"
              width="100%"
            />
          </div>
          <div className="col-md-4 mb-3">
            <Label editorId="collection-promise-amount">Promise Amount</Label>
            <NumericTextBox
              id="collection-promise-amount"
              value={form.PromiseAmount}
              min={0}
              format="c2"
              spinners={false}
              onChange={event => setForm(current => ({ ...current, PromiseAmount: event.value }))}
              width="100%"
            />
          </div>
        </div>

        <div className="mb-3">
          <Checkbox
            id="collection-contact-reached"
            checked={form.ContactReached}
            onChange={event => setForm(current => ({ ...current, ContactReached: Boolean(event.value) }))}
            label="Contact reached or responded"
          />
        </div>

        <div className="mb-2">
          <Label editorId="collection-note">Notes</Label>
          <TextArea
            id="collection-note"
            value={form.Note}
            rows={6}
            onChange={event => setForm(current => ({ ...current, Note: event.value }))}
            style={{ width: '100%' }}
          />
        </div>

      </form>

      <DialogActionsBar layout="end">
        <Button type="button" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="button" themeColor="primary" disabled={saving} onClick={() => save()}>
          {saving ? 'Saving...' : 'Save Activity'}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CollectionActivityDialog;
